import { motion } from "framer-motion";
import { Apple, Play, Download as DownloadIcon, Smartphone } from "lucide-react";
import Logo from "./Logo";
import Reveal from "./Reveal";
import { useLang } from "../i18n/i18n";
import { track } from "../lib/track";

const BG =
  "https://images.unsplash.com/photo-1762463176312-1757d5125c85?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function DownloadApp({ settings }) {
  const { t } = useLang();
  const s = settings || {};
  const links = [
    { id: "play", icon: Play, label: "Google Play", url: s.play_store_url },
    { id: "appstore", icon: Apple, label: "App Store", url: s.app_store_url || "https://apps.apple.com/us/app/instagoldcalc/id6824466775" },
    { id: "apk", icon: DownloadIcon, label: "APK", url: s.apk_url },
  ];

  return (
    <section id="download" className="relative py-28 overflow-hidden" data-testid="download-section">
      <div className="absolute inset-0">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-panel/60 to-ink" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid gap-16 lg:grid-cols-[1.2fr_1fr] items-center">
        <Reveal>
          <p className="text-xs tracking-[0.35em] uppercase text-gold">{t("dlapp.eyebrow")}</p>
          <h2 className="mt-4 font-display text-4xl sm:text-6xl font-light text-cream leading-tight">
            InstaGold Calc,
            <br />
            <em className="gold-text italic font-medium">{t("dlapp.title2")}</em>
          </h2>
          <p className="mt-6 max-w-lg text-base sm:text-lg leading-relaxed text-cream/70">{t("dlapp.sub")}</p>

          <div className="mt-10 flex flex-wrap gap-4">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.url || "#"}
                target={l.url ? "_blank" : undefined}
                rel="noreferrer"
                onClick={(e) => {
                  if (!l.url) e.preventDefault();
                  else track("download", l.id);
                }}
                data-testid={`download-${l.id}-btn`}
                className={`inline-flex items-center gap-3 rounded-xl border px-6 py-4 transition-all ${
                  l.url
                    ? "border-gold/40 bg-gold/10 text-goldlight hover:bg-gold hover:text-ink hover:shadow-[0_0_30px_rgba(212,175,55,0.35)]"
                    : "border-cream/12 text-mutedc cursor-not-allowed"
                }`}
              >
                <l.icon size={20} />
                <span className="text-left leading-tight">
                  <span className="block text-[10px] uppercase tracking-[0.2em] opacity-70">
                    {l.url ? t("dlpage.downloadOn") : t("dlpage.soonOn")}
                  </span>
                  <span className="block text-sm font-semibold">{l.label}</span>
                </span>
              </a>
            ))}
          </div>

          {s.download_note && (
            <p className="mt-6 text-sm text-mutedc" data-testid="download-note">
              {s.download_note}
            </p>
          )}
          <a
            href="/telecharger"
            data-testid="download-qr-page-link"
            className="mt-5 inline-flex items-center gap-2 text-sm text-goldlight underline decoration-gold/40 underline-offset-4 hover:decoration-goldlight transition-all"
          >
            {t("dlapp.qrLink")}
          </a>
        </Reveal>

        <Reveal delay={0.15} className="flex justify-center">
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }}
            className="relative w-[270px] rounded-[2.8rem] border border-gold/30 bg-ink p-3 shadow-[0_40px_100px_rgba(0,0,0,0.7)]"
            data-testid="download-phone-mock"
          >
            <div className="rounded-[2.2rem] bg-gradient-to-b from-panel2 via-panel to-ink px-6 pt-10 pb-12">
              <div className="mx-auto mb-8 h-5 w-24 rounded-full bg-ink border border-cream/10" />
              <Logo />
              <div className="mt-8 rounded-2xl border border-gold/20 bg-ink/70 p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-goldlight/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {t("calc.gold")} 24 K — 192,22 $/g
                </div>
                <p className="mt-3 font-display text-3xl text-cream tabular">192,22 $/g</p>
              </div>
              <div className="mt-4 flex gap-2">
                {["24K", "18K", "14K", "10K"].map((k) => (
                  <span key={k} className="flex-1 rounded-lg border border-gold/15 py-2 text-center text-[11px] text-cream/70">
                    {k}
                  </span>
                ))}
              </div>
              <div className="mt-4 rounded-2xl bg-gold/90 p-5 text-center">
                <p className="text-[10px] uppercase tracking-[0.25em] text-ink/70">{t("calc.estimate")}</p>
                <p className="font-display text-2xl text-ink tabular">2 598,97 $</p>
              </div>
              <div className="mt-6 flex items-center justify-center gap-2 text-mutedc">
                <Smartphone size={14} />
                <span className="text-[10px] tracking-[0.3em] uppercase">Montréal</span>
              </div>
            </div>
          </motion.div>
        </Reveal>
      </div>
    </section>
  );
}
